import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "setting-dashboard-alert-page" }
const _hoisted_2 = { class: "setting-dashboard-alert-page__header" }
const _hoisted_3 = { class: "container__title" }
const _hoisted_4 = { class: "setting" }
const _hoisted_5 = { class: "input__title" }
const _hoisted_6 = { class: "input__items" }
const _hoisted_7 = { class: "button__container" }

import { i18n } from '@/i18n';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  DashboardAlertDirections,
  DashboardAlertSeverities,
  DashboardAlertSetting,
  DashboardAlertSettingValueType,
  DashboardAlertSeverity,
  DashboardAlertDirection,
  dashboardAlertSettingValidation,
  DEFAULT_DASHBOARD_ALERT_SETTING,
  DashboardAlertSettingValueTypes,
} from '../domains/entities/DashboardAlert';
import AlertInputConsecutive from '../components/alertInput/AlertInputConsecutive.vue';
import AlertInputSharp from '../components/alertInput/AlertInputSharp.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { DashboardAlertSettingFactory } from '../domains/factories/DashboardAlertSettingFactory';
import { diContainer } from '@/coreContainer/container';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import ErrorMessage from '@/corePagesModule/components/ErrorMessage.vue';
import { useRouter } from 'vue-router';
import { simpleCopy } from '@/utils/deepCopy';
import AlertInputCurrent from '../components/alertInput/AlertInputCurrent.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'SettingDashboardAlertPage',
  setup(__props) {

const t = i18n.t;
const router = useRouter();

const text = {
  excess: {
    title: '過剰アラートの設定',
    critical: {
      consecutive: { key: 'dashboard:アラート条件:nヶ月連続でm%以下', option: { n: 2, m: 50 } },
      sharp: { key: 'dashboard:アラート条件:前回受領内示比n%以下かつ差分数量m以上', option: { n: 50, m: 100 } },
    },
    warning: {
      consecutive: { key: 'dashboard:アラート条件:nヶ月連続でm%以下', option: { n: 2, m: 80 } },
      sharp: { key: 'dashboard:アラート条件:前回受領内示比n%以下かつ差分数量m以上', option: { n: 75, m: 100 } },
    },
  },
  shortage: {
    title: '不足アラートの設定',
    critical: {
      consecutive: { key: 'dashboard:アラート条件:nヶ月連続でm%以上', option: { n: 2, m: 150 } },
      sharp: { key: 'dashboard:アラート条件:前回受領内示比n%以上かつ差分数量m以上', option: { n: 200, m: 100 } },
      current: { key: 'dashboard:アラート条件:内示に対して当月の出荷済み数量がn%を超える', option: { n: 100 } },
    },
    warning: {
      consecutive: { key: 'dashboard:アラート条件:nヶ月連続でm%以上', option: { n: 2, m: 120 } },
      sharp: { key: 'dashboard:アラート条件:前回受領内示比n%以上かつ差分数量m以上', option: { n: 125, m: 100 } },
    },
  },
};

/**
 * デフォルト値表示用の条件文を生成する
 * @param key - 条件文の翻訳キー
 * @param option - 条件文へ埋め込むデフォルト値
 */
const defaultValueString = (key: string, option: { n: number; m?: number }) => t('dashboard:デフォルト', { condition: t(key, option) });

const inputValues = ref<DashboardAlertSetting>(DashboardAlertSettingFactory.create());
const isEdited = ref(false);
const errorMessage = ref<DashboardAlertSetting>(DashboardAlertSettingFactory.create());
const bySeverityDirectionErrorMessage = computed(() =>
  Object.fromEntries(
    DashboardAlertSeverities.map((severity) => [
      severity,
      Object.fromEntries(
        DashboardAlertDirections.map((direction) => [
          direction,
          Object.values(errorMessage.value[severity][direction]).filter((s) => s !== '')[0],
        ])
      ),
    ])
  )
);
const isError = computed(() => Object.values(bySeverityDirectionErrorMessage.value).some((dv) => Object.values(dv).some((es) => es)));

/**
 * Stateをリセットする
 */
const resetState = () => {
  inputValues.value = DashboardAlertSettingFactory.create();
  errorMessage.value = DashboardAlertSettingFactory.create();
  isEdited.value = false;
};

/**
 * データフェッチ
 */
const fetch = async () => {
  const res = await waitLoading(diContainer.getDashboardAlertSettingUseCase.execute(), undefined, '');
  if (!res) {
    await showInfoModal({
      icon: 'error',
      modalTitle: t('取得に失敗しました'),
      modalDescription: t('情報の取得に失敗しました。リロードしてください。'),
      backButtonLabel: 'OK',
      isDisabledNextButton: true,
    });
    return;
  }
  switch (res.status.type) {
    case 'success':
      inputValues.value = res.status.data;
      return;
    default:
      await showInfoModal({
        icon: 'error',
        modalTitle: t('取得に失敗しました'),
        modalDescription: t('情報の取得に失敗しました。リロードしてください。'),
        backButtonLabel: 'OK',
        isDisabledNextButton: true,
      });
  }
};

/**
 * Inputハンドラ
 * @param e - Inputイベント
 * @param severity - 危険度
 * @param direction - 向き
 * @param valueType - 値種類
 */
const onInput = async (
  e: Event,
  severity: DashboardAlertSeverity,
  direction: DashboardAlertDirection,
  valueType: DashboardAlertSettingValueType
) => {
  isEdited.value = true;
  const x = (e.target as HTMLInputElement).value;
  inputValues.value[severity][direction][valueType] = x;
  errorMessage.value[severity][direction][valueType] = dashboardAlertSettingValidation(severity, direction, x, valueType);
};

/**
 * 保存ハンドラ
 */
const onClickSave = async () => {
  for (const severity of DashboardAlertSeverities) {
    for (const direction of DashboardAlertDirections) {
      for (const valueType of DashboardAlertSettingValueTypes) {
        errorMessage.value[severity][direction][valueType] = dashboardAlertSettingValidation(
          severity,
          direction,
          inputValues.value[severity][direction][valueType],
          valueType
        );
      }
    }
  }
  await nextTick();
  if (isError.value) {
    return;
  }
  await save();
};

/**
 * リセットハンドラ
 */
const onReset = async () => {
  const confirmReset = await showInfoModal({
    icon: 'info',
    modalTitle: t('デフォルトの設定に戻してよろしいですか？（このまま保存されます）'),
    backButtonLabel: 'キャンセル',
    nextButtonLabel: 'OK',
    noDescription: true,
  });
  if (!confirmReset) return;
  inputValues.value = simpleCopy(DEFAULT_DASHBOARD_ALERT_SETTING);
  await save();
};

/**
 * 保存
 */
const save = async () => {
  const result = await waitLoading(diContainer.putDashboardAlertSettingUseCase.execute(inputValues.value));
  if (!result?.status.type || result.status.type === 'error') {
    showInfoModal({
      icon: 'error',
      colorStyle: 'error',
      modalTitle: t('保存に失敗しました'),
      modalDescription: t('時間をおいて再度お試しください。'),
      backButtonLabel: 'OK',
      isDisabledNextButton: true,
    });
    return;
  }
  const wantBackToAlertPage = await showInfoModal({
    modalTitle: t('保存しました'),
    modalDescription: t('ダッシュボードに戻りますか？'),
    backButtonLabel: t('キャンセル'),
    nextButtonLabel: t('ダッシュボードに戻る'),
  });
  if (wantBackToAlertPage) {
    router.push('/inventory-dashboard');
  } else {
    resetState();
    await fetch();
  }
};

onMounted(async () => {
  await fetch();
});

onBeforeUnmount(() => {
  resetState();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createVNode(SettingsPageTitle, {
        label: _unref(t)('設定 > 発注・計算ロジック > ダッシュボードアラート設定')
      }, null, 8, ["label"])
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(DashboardAlertDirections), (direction) => {
      return (_openBlock(), _createElementBlock("div", {
        key: direction,
        class: "setting-dashboard-alert-page__container"
      }, [
        _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)(text[direction].title)), 1),
        _createElementVNode("div", _hoisted_4, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(DashboardAlertSeverities), (severity) => {
            return (_openBlock(), _createElementBlock("div", {
              key: severity,
              class: "setting__input"
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(`input__container input__container--${severity}`)
              }, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("span", null, _toDisplayString(_unref(t)(`dashboard:${severity}`)), 1),
                  _createVNode(ErrorMessage, {
                    "is-error": !!bySeverityDirectionErrorMessage.value[severity][direction],
                    "error-message": bySeverityDirectionErrorMessage.value[severity][direction]
                  }, null, 8, ["is-error", "error-message"])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(AlertInputSharp, {
                    severity: severity,
                    direction: direction,
                    "mom-change-rate-threshold": inputValues.value[severity][direction].momChangeRateThreshold,
                    "min-quantity-threshold": inputValues.value[severity][direction].minQuantityThreshold,
                    "default-value-string": defaultValueString(text[direction][severity].sharp.key, text[direction][severity].sharp.option),
                    "onUpdate:momChangeRateThreshold": (e) => onInput(e, severity, direction, 'momChangeRateThreshold'),
                    "onUpdate:minQuantityThreshold": (e) => onInput(e, severity, direction, 'minQuantityThreshold')
                  }, null, 8, ["severity", "direction", "mom-change-rate-threshold", "min-quantity-threshold", "default-value-string", "onUpdate:momChangeRateThreshold", "onUpdate:minQuantityThreshold"]),
                  _createVNode(AlertInputConsecutive, {
                    direction: direction,
                    "consecutive-months": inputValues.value[severity][direction].consecutiveMonths,
                    "ratio-threshold": inputValues.value[severity][direction].ratioThreshold,
                    "default-value-string": 
                  defaultValueString(text[direction][severity].consecutive.key, text[direction][severity].consecutive.option)
                ,
                    "onUpdate:consecutiveMonths": (e) => onInput(e, severity, direction, 'consecutiveMonths'),
                    "onUpdate:ratioThreshold": (e) => onInput(e, severity, direction, 'ratioThreshold')
                  }, null, 8, ["direction", "consecutive-months", "ratio-threshold", "default-value-string", "onUpdate:consecutiveMonths", "onUpdate:ratioThreshold"]),
                  (severity === 'critical' && direction === 'shortage')
                    ? (_openBlock(), _createBlock(AlertInputCurrent, {
                        key: 0,
                        threshold: inputValues.value[severity][direction].currentMonthForecastAccuracyThreshold,
                        "default-value-string": defaultValueString(text[direction][severity].current.key, text[direction][severity].current.option),
                        "onUpdate:threshold": (e) => onInput(e, severity, direction, 'currentMonthForecastAccuracyThreshold')
                      }, null, 8, ["threshold", "default-value-string", "onUpdate:threshold"]))
                    : _createCommentVNode("", true)
                ])
              ], 2)
            ]))
          }), 128))
        ])
      ]))
    }), 128)),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(TextButton, {
        disabled: !isEdited.value || isError.value,
        onClick: onClickSave
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('保存')), 1)
        ], undefined, true),
        _: 1
      }, 8, ["disabled"]),
      _createElementVNode("button", {
        class: "button--back",
        onClick: onReset
      }, _toDisplayString(_unref(t)('デフォルトに戻す')), 1)
    ])
  ]))
}
}

})